/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import {
  baseColumnProps,
  formatFunction,
  isBaseColumnPropertyVisible,
} from '../BaseColumn';
import { defaultValue } from './StringColumn';
import { analyzeStringColumn } from './StringColumnAnalyze';

const StringColumnManager: ComponentManagerFn<'StringColumn'> = (
  platform: Platform,
): ComponentManager<'StringColumn'> => {
  return {
    ...baseComponentManager<'StringColumn'>(platform, 'StringColumn'),
    type: 'StringColumn',
    name: 'String Column',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ColumnFooter',
          'TriggerOnBlur',
          'TriggerOnCellClick',
          'TriggerOnChange',
          'TriggerOnFocus',
          'TriggerOnValueChange',
          'TriggerOnPaste',
          'TriggerOnEnterKeyPress',
          'TriggerOnDeleteKeyPress',
          'TriggerAfterPaste',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        aliasType: ({ appUid, pageId, itemId }) => {
          const parent = platform.findParentOfType(
            appUid,
            pageId,
            itemId,
            'LOVGrid',
          );
          return parent ? 'LOV' : 'STORE';
        },
        getAttributeDataTypes: ({ props }) => [
          'String',
          'Email',
          'Phone',
          'URL',
          'Textarea',
          'EncryptedString',
          'UserID',
          'YN', // allow YN & bool to be used with format function
          'TF',
          'Boolean',
          'JSON',
        ],
      },
      minLength: {
        label: 'Min Length',
        type: 'numberInput',
        category: 'Validation',
      },
      maxLength: {
        label: 'Max Length',
        type: 'numberInput',
        category: 'Validation',
      },
      regex: {
        label: 'Regular Expression',
        type: 'string',
        category: 'Validation',
      },
      maxUniqueVals: {
        label: 'Max Unique Values',
        type: 'numberInput',
        helpText: 'Used to limit unique options for choose value filters',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
        helpText: 'Text to display when the field is empty',
      },
      renderLetterAvatar: {
        category: 'Look & Feel',
        label: 'Letter Avatar',
        type: 'select',
        options: { off: 'Off', on: 'On', avatarOnly: 'Avatar Only' },
      },
      avatarColorExpression: {
        category: 'Look & Feel',
        label: 'Avatar Text Color',
        type: 'ColorExpression',
        placeholder: "e.g. '#FFFFFF'",
      },
      avatarBgColorExpression: {
        category: 'Look & Feel',
        label: 'Avatar Background Color',
        type: 'ColorExpression',
        placeholder: "e.g. '#157CEC'",
      },
      avatarSize: {
        category: 'Look & Feel',
        label: 'Avatar Size',
        type: 'number',
        min: 16,
        max: 80,
        step: 4,
      },
      renderAsPill: {
        category: 'Look & Feel',
        label: 'Render As Pill',
        type: 'boolean',
      },
      serverValidateOnChange: {
        category: 'Validation',
        label: 'Server Validate On Change',
        type: 'boolean',
      },
      formatFunction,
      caseInsensitiveHeaderFilter: {
        category: 'Header',
        label: 'Case Insensitive Filter',
        type: 'boolean',
      },
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      const item = platform.getItem(appUid, pageId, itemId);
      if (!item) {
        platform.showError(`Missing column with ID: ${itemId}!`);
        return false;
      }
      const parent = platform.getItem(appUid, pageId, item.parent);
      if (!parent) {
        platform.showError(`Missing column parent with ID: ${item.parent}!`);
        return false;
      }
      if (parent.type === 'LOVGrid') {
        switch (prop) {
          case 'minLength':
          case 'maxLength':
          case 'regex':
            return false;
          default:
        }
      }
      if (parent.type !== 'CanvasGrid') {
        switch (prop) {
          case 'renderAsPill':
          case 'renderLetterAvatar':
          case 'avatarColorExpression':
          case 'avatarBgColorExpression':
          case 'avatarSize':
            return false;
          default:
        }
      }

      switch (prop) {
        case 'avatarColorExpression':
        case 'avatarBgColorExpression':
        case 'avatarSize':
          return itemData.renderLetterAvatar !== 'off';
        case 'alignment':
          return itemData.renderLetterAvatar !== 'on';
        case 'renderAsPill':
          return itemData.renderLetterAvatar === 'off';
        default:
      }

      return true;
    },
    generateShortId: (item) => item.label?.value,
    analyze: analyzeStringColumn,
    replaceableWith: [
      'CheckboxColumn',
      'DateColumn',
      'LOVColumn',
      'LookupSelectColumn',
      'NumberColumn',
      'SelectColumn',
      'UserPicColumn',
    ],
    getDisplayText: (item) => item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-text-input', iconType: 'cloudio' },
  };
};

export default StringColumnManager;
